import React, { useCallback, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import TransactionRow from "../components/TransactionRow";
import EditTransaction from "../containers/EditTransaction";
import { putGroup } from "../services/AnimalSplitCore";
import { deleteTransaction } from "../services/AnimalSplitCore";
import { toggleModal } from "../utils/utils";
import { MODAL_ID } from "../constants";
import { useTranslation } from "react-i18next";
import ConfirmModal from "../components/ConfirmModal";
import "./GroupPageCards.css";

export default function TransactionList(props) {
  const { t } = useTranslation();
  const {
    groupId,
    contributors,
    transactions,
    renderGroup,
    sortOrder,
    setIsLoading,
    isSettled = false,
  } = props;
  const modalId = "edit-transaction-modal";
  const deleteConfirmModalId = "delete-transaction-confirm-modal";
  const dropdownElement = document.getElementById("transaction-sort-dropdown");
  const dropdownInstance = dropdownElement
    ? new window.bootstrap.Dropdown(dropdownElement)
    : undefined;

  const contributorIdDict = {};
  contributors.forEach((c) => (contributorIdDict[c.id] = c));
  transactions.forEach(
    (t) => (t.contributor = contributorIdDict[t.contributorId])
  );

  const [newTransaction, setNewTransaction] = useState(false);
  const [targetTransaction, setTargetTransaction] = useState({});
  const [collapse, setCollapse] = useState(false);
  const [pendingDeleteTransaction, setPendingDeleteTransaction] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const collapseCard = document.getElementById("collapseCardTransaction");
    var bsCollapseCard = new window.bootstrap.Collapse(collapseCard, {
      toggle: false,
    });
    collapse ? bsCollapseCard.hide() : bsCollapseCard.show();
  }, [collapse]);

  const sortType = {
    default: t("transaction.sort_time"),
    contributor: t("participant.title"),
    amount: t("transaction.amount"),
    category: t("transaction.category_title"),
  };
  const sortIcon = {
    default: "bi bi-clock",
    contributor: "bi bi-people",
    amount: "bi bi-currency-dollar",
    category: "bi bi-tag",
  };

  //get transactions from backend when transactions updated
  const [displayTransactions, setDisplayTransactions] = useState([]);

  const [isReversed, setIsReversed] = useState(false);
  useEffect(() => {
    setDisplayTransactions(transactions);
    setIsReversed(false);
  }, [transactions, sortOrder]);

  useEffect(() => {
    var TransactionRowDisplay = document.getElementById(
      "transaction-row-display"
    );
    for (let i = 0; i < TransactionRowDisplay.childNodes.length; i++)
      TransactionRowDisplay.appendChild(TransactionRowDisplay.childNodes[i]);
  }, [isReversed]);

  const handleSave = async (selectedOrder) => {
    try {
      setIsLoading(true);
      const { payload } = await putGroup(groupId, {
        sortOrder: selectedOrder,
      });
      setIsLoading(false);
      renderGroup(payload);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    }
  };

  const closeModalById = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el || !window?.bootstrap?.Modal) return;
    try {
      // Bootstrap 5.0.x compatibility (no getOrCreateInstance)
      const modal = window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
      modal.hide();
    } catch (e) {
      document.querySelectorAll(".modal-backdrop").forEach((b) => b.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("padding-right");
      document.body.style.removeProperty("overflow");
    }
  }, []);

  const handleRequestDelete = useCallback(
    (transaction) => {
      if (isSettled) return;
      setPendingDeleteTransaction(transaction);
      // Ensure the transaction modal is fully hidden before opening confirm
      // (prevents stacked backdrops and "frozen" scroll).
      const openConfirm = () => {
        const el = document.getElementById(deleteConfirmModalId);
        if (!el || !window?.bootstrap?.Modal) return;
        try {
          const inst =
            window.bootstrap.Modal.getInstance(el) || new window.bootstrap.Modal(el);
          inst.show();
        } catch (e) {
          // fallback to legacy helper
          toggleModal(deleteConfirmModalId);
        }
      };

      const txEl = document.getElementById(modalId);
      const txIsOpen = !!txEl && txEl.classList.contains("show");
      if (txEl && txIsOpen && window?.bootstrap?.Modal) {
        try {
          txEl.addEventListener("hidden.bs.modal", openConfirm, { once: true });
          const inst =
            window.bootstrap.Modal.getInstance(txEl) ||
            // Bootstrap 5.0.x compatibility (no getOrCreateInstance)
            window.bootstrap.Modal.getInstance(txEl) || new window.bootstrap.Modal(txEl);
          inst.hide();
          return;
        } catch (e) {
          // fall through
        }
      }

      // Not open (or cannot detect) — open confirm directly.
      openConfirm();
    },
    [deleteConfirmModalId, isSettled, modalId]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!groupId || !pendingDeleteTransaction?.id || isSettled || isDeleting) return;
    setIsDeleting(true);
    try {
      setIsLoading(true);
      const { payload } = await deleteTransaction(groupId, pendingDeleteTransaction.id);
      renderGroup(payload);
      closeModalById(deleteConfirmModalId);
      setPendingDeleteTransaction(null);
    } catch (error) {
      console.error(error);
      toggleModal(MODAL_ID.CONNECTION_ERROR);
    } finally {
      setIsLoading(false);
      setIsDeleting(false);
    }
  }, [
    groupId,
    pendingDeleteTransaction,
    isSettled,
    isDeleting,
    setIsLoading,
    renderGroup,
    closeModalById,
    deleteConfirmModalId,
  ]);

  const editTransactionModal = (
    <EditTransaction
      newTransaction={newTransaction}
      transaction={targetTransaction}
      modalId={modalId}
      groupId={groupId}
      contributors={contributors}
      renderGroup={renderGroup}
      setIsLoading={setIsLoading}
      setNewTransaction={setNewTransaction}
      isSettled={isSettled}
    />
  );

  return (
    <>
    <div className="card shadow-sm as-card as-theme position-relative">
      <div
        className="card-header cursor-pointer d-flex justify-content-between align-items-center flex-wrap as-card-header"
        onClick={() => setCollapse(!collapse)}
      >
        <div className="as-card-header-title">
          <span className="as-icon-badge" aria-hidden="true">
            🧾
          </span>
          <strong>{t("transaction.title")}</strong>
        </div>
        <div className="as-header-actions">
          <small className="me-1">{t("transaction.sort")}</small>
          <button
            className="dropdownsortDropdown btn btn-sm as-btn-soft btn-block"
            id="transaction-sort-dropdown"
            data-bs-toggle="dropdown"
            onClick={(e) => {
              e.stopPropagation();
              dropdownInstance.toggle();
            }}
          >
            <div className={sortIcon[sortOrder]}></div>
          </button>
          <button
            className={`sortDropdown btn btn-sm as-btn-soft ms-1 me-2`}
            onClick={(e) => {
              e.stopPropagation();
              setIsReversed(!isReversed);
            }}
          >
            <div
              className={`${isReversed ? "bi bi-sort-up" : "bi bi-sort-down"}`}
            ></div>
          </button>
          <div
            className="dropdown-menu as-dropdown-menu"
            onClick={(e) => {
              e.stopPropagation();
              dropdownInstance.toggle();
            }}
          >
            {Object.keys(sortType).map((k) => (
              <button
                className={`dropdown-item d-flex align-items-center ${
                  sortOrder === k ? "active" : ""
                }`}
                value={k}
                onClick={(e) => {
                  //e.stopPropagation();//for test use only
                  handleSave(k);
                }}
                key={k}
              >
                <div className={sortIcon[k]}></div>
                <div className="mx-2">{sortType[k]}</div>
              </button>
            ))}
          </div>
          <i
            className={`${collapse ? "bi bi-chevron-down" : "bi bi-chevron-up"}`}
          ></i>
        </div>
      </div>
      <div className="collapse show" id="collapseCardTransaction">
        <ul
          className="list-group list-group-flush as-row-list"
          id="transaction-row-display"
        >
          {displayTransactions.map((t) => {
            return (
              <li className="list-group-item" key={t.id}>
                <TransactionRow
                  modalId={modalId}
                  setTargetTransaction={setTargetTransaction}
                  groupId={groupId}
                  transaction={t}
                  renderGroup={renderGroup}
                  setNewTransaction={setNewTransaction}
                  setIsLoading={setIsLoading}
                  isSettled={isSettled}
                  onRequestDelete={handleRequestDelete}
                />
              </li>
            );
          })}
        </ul>
        <div className="card-body">
          <button
            id="create-transaction-fab"
            className={`btn btn-primary position-absolute top-100 end-0 translate-middle-y me-3 shadow round as-fab ${
              collapse ? "invisible" : "visible"
            }`}
            data-bs-toggle={isSettled ? undefined : "modal"}
            data-bs-target={isSettled ? undefined : `#${modalId}`}
            onClick={() => {
              if (isSettled) return;
              setNewTransaction(true);
            }}
            disabled={isSettled}
            aria-label={
              isSettled
                ? (t("group.settled") || "Settled")
                : (t("transaction.create_transaction") || "Create transaction")
            }
            title={
              isSettled
                ? (t("group.settled") || "Settled")
                : (t("transaction.create_transaction") || "Create transaction")
            }
          >
            <i className={isSettled ? "bi-lock-fill" : "bi-plus-lg"} />
          </button>
        </div>
      </div>
    </div>
    {typeof document !== "undefined" ? createPortal(editTransactionModal, document.body) : editTransactionModal}
    {typeof document !== "undefined"
      ? createPortal(
          <ConfirmModal
            modalId={deleteConfirmModalId}
            icon="🗑️"
            title={t("transaction.delete_confirm_title") || "Delete this transaction?"}
            body={
              <div>
                <p className="mb-2">
                  {t("transaction.delete_confirm_body") ||
                    "This will permanently delete the transaction and update the group's balances."}
                </p>
                {pendingDeleteTransaction?.title ? (
                  <p className="mb-0">
                    <strong>{pendingDeleteTransaction.title}</strong>
                  </p>
                ) : null}
              </div>
            }
            cancelText={t("general.cancel") || "Cancel"}
            confirmText={
              isDeleting ? (t("general.deleting") || "Deleting…") : (t("general.delete") || "Delete")
            }
            confirmVariantClassName="btn-danger"
            onConfirm={handleConfirmDelete}
            confirmDisabled={!pendingDeleteTransaction?.id || isSettled || isDeleting}
            cancelDisabled={isDeleting}
          />,
          document.body
        )
      : null}
    </>
  );
}
